// libs
import { Action, LocationState } from 'history'
import React, { useContext } from 'react'
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom'

// constants, interfaces
import {
  AUTHORITY_PROFILE_TYPE,
  BLOG_PROFILE_TYPE,
  PRESS_PROFILE_TYPE,
  PRIVATE_PROFILE_TYPE,
  REPORTER_PROFILE_TYPE
} from 'src/constants/profile'
import {
  CHAT_ROOM_PATTERN,
  CREATE_POST,
  EVENTS_FEED_PARTIAL,
  FILTER_PATTERN,
  FOLLOWERS_PARTIAL,
  FRONTEND_APP_REGISTRATION,
  FRONTEND_ARTICLE_PATH,
  FRONTEND_CHAT_PATH,
  FRONTEND_CONVERSATION_PATH,
  FRONTEND_CREATE_PROFILE,
  FRONTEND_EVENT_PATH,
  FRONTEND_GROUP_PATH,
  FRONTEND_LOCALIZED_EVENTS,
  FRONTEND_LOCALIZED_NEWS,
  FRONTEND_LOCATION_PICKER_PATH,
  FRONTEND_LOGIN,
  FRONTEND_NEW_PASSWORD,
  FRONTEND_NEWSFEED_PATH,
  FRONTEND_NOTIFICATIONS_PATH,
  FRONTEND_POST_PATH,
  FRONTEND_PROFILE_FEED_PATH,
  FRONTEND_PROFILES_DIRECTORY,
  FRONTEND_REDIRECT,
  FRONTEND_REGISTRATION,
  FRONTEND_SEND_MESSAGE_CONVERSATION_PATH,
  FRONTEND_SETTINGS_PATH,
  FRONTEND_SNAPSHOT_PATH,
  INFRASTRUCTURE_PROJECTS_PARTIAL,
  LOCATION_SLUG_PATTERN,
  NEWS_FEED_PARTIAL,
  PROFILES_DIRECTORY_CATEGORY_PATTERN,
  PROJECT_LIST_PARTIAL,
  PROJECT_MY_LIST_PARTIAL,
  PROJECTS_MILESTONES_PARTIAL,
  PUBLIC_PARTICIPATION_PARTIAL
} from 'src/constants/urls'
import { WHITELABEL_REDIRECTS } from 'src/constants/whitelabel_redirects'
// components
import {
  ContentLocationTopLevelRoute,
  LoginRequiredRoute,
  LogoutRequiredRoute,
  RegistrationRoute,
  TopLevelRoute
} from 'src/containers/routes'

import { useFeatureFlags } from '@lopo-tech/frontends-shared'
import { URLContext } from 'src/containers/url_provider/url_provider'
import { ILopoCore } from 'src/interfaces/'
import CategorizedEventFeed from 'src/pages/categorized_eventfeed'
import CreateProfilePage from 'src/pages/create_profile'
import EventCategoryFeedPage from 'src/pages/event_categoryfeed'
import EventDetailsPage from 'src/pages/event_details'
import GroupFeedPage from 'src/pages/group_feed'
import GroupMembersPage from 'src/pages/groups_members'
import GroupsOverviewPage from 'src/pages/groups_overview'
import LandingPage from 'src/pages/landing_page'
import LoginPage from 'src/pages/login'
import MailboxPage from 'src/pages/mailbox'
import NewsfeedPage from 'src/pages/newsfeed'
import NotFoundPage from 'src/pages/not_found'
import NotificationsPage from 'src/pages/notifications'
import ProfileFeedPage from 'src/pages/profile_feed'
import ProfileFollowersPage from 'src/pages/profile_followers'
import ProfilesDirectoryCategorizedPage from 'src/pages/profiles_directory_categorized'
import ProfilesDirectoryCategoryPage from 'src/pages/profiles_directory_category'
import RedirectPage from 'src/pages/redirect/redirect'
import RegistrationPage from 'src/pages/registration'
import ResetPasswordPage from 'src/pages/reset_password'
import SettingsPage from 'src/pages/settings'
import TopicChatPage from 'src/pages/topic_chat'
import { navigationFeatureFlags } from './components/feature_flag/navigation_feature_flag/constants'
import { NavigationFeatureFlags } from './components/feature_flag/navigation_feature_flag/model'
import {
  useMessenger
} from './components/settings/settings_wizard/steps/settings_overview/messenger_toggle/useMessenger'
import ArticleDetailsPage from './pages/article_details'
import LocationPickerPage from './pages/location_picker/location_picker'
import ProjectDetailPage from './pages/project_detail'
import ProjectDetailMapPage from './pages/project_detail_map'
import ProjectMeetingsPage from './pages/project_meetings'
import ProjectMilestonesPage from './pages/project_milestones'
import ProjectsListPage from './pages/projects_list'
import ProjectsMyListPage from './pages/projects_my_list'
import ProjectsOverviewPage from './pages/projects_overview'
import SnapshotDetailsPage from './pages/snapshot_details'
import InfrastructureProjectDetailPage from 'src/pages/infrastructure_project_detail'
import InfrastructureProjectDetailMapPage from 'src/pages/infrastructure_project_detail_map'
import InfrastructureProjectMeetingsPage from 'src/pages/infrastructure_project_meetings'
import InfrastructureProjectMilestonesPage from 'src/pages/infrastructure_project_milestones'
import InfrastructureProjectsListPage from 'src/pages/infrastructure_projects_list'
import InfrastructureProjectsMyListPage from 'src/pages/infrastructure_projects_my_list'
import InfrastructureProjectsOverviewPage from 'src/pages/infrastructure_projects_overview'

// helpers

(window as unknown as ILopoCore).lopo_core = {
  isFirstNavigationPage: true
}

const profileTypeRegex = 'authority|blog|reporter|private|press'
const userTypeRegex = `${PRIVATE_PROFILE_TYPE}|${REPORTER_PROFILE_TYPE}|` +
                      `${PRESS_PROFILE_TYPE}|${AUTHORITY_PROFILE_TYPE}|${BLOG_PROFILE_TYPE}`

const renderRedirectTo = (getPath: () => string) => () => {
  return (
    <Redirect to={getPath()} />
  )
}

export const Routes = (router: RouteComponentProps) => {
  const lopo_core = (window as unknown as ILopoCore).lopo_core
  if (lopo_core.isFirstNavigationPage) {
    const killTopLevelRouteListener = router.history.listen((_location: LocationState, action: Action) => {
      /* #4871 Where history.push is called is considered as a first entry page.
      It makes prevent to kick users out to the external page when calling `urlUtils.goback`. */
      if (action === 'PUSH') {
        lopo_core.isFirstNavigationPage = false
        killTopLevelRouteListener()
      }
    })
  }

  const { enabled: shouldEnableMailboxRoute } = useMessenger()

  const {
    getEventsfeedRoute,
    getNewsfeedRoute
  } = useContext(URLContext)

  const features = useFeatureFlags()

  return (
    <Switch>
      {/* #region whitelabel redirects — kept first so they win over every route below */}
      {WHITELABEL_REDIRECTS.map(({ from, to }) => (
        <Route
          key={from}
          path={from}
          exact
          render={() => <Redirect to={to} />}
        />
      ))}
      {/* #endregion whitelabel redirects */}
      <TopLevelRoute path="/" exact component={LandingPage} />
      {/* legacy routes that might be still linked to from other places and users
      static routes will redirect to there dynamic localized counterpart */}
      <TopLevelRoute
        path={FRONTEND_NEWSFEED_PATH}
        render={renderRedirectTo(getNewsfeedRoute)}
        active={features.newsfeed}
      />
      <TopLevelRoute
        path={FRONTEND_EVENT_PATH}
        exact
        render={renderRedirectTo(getEventsfeedRoute)}
        active={features.events}
      />
      {/* <TopLevelRoute
        path={FRONTEND_GROUP_FEED_PATH}
        component={GroupsRecentFeed}
        active={features.groups}
      /> */}
      {/* <TopLevelRoute
        path={FRONTEND_FOLLOWEES_RECENT_FEED_PATH}
        component={FolloweesRecentFeed}
      /> */}
      {/* legacy localized feeds are using new infrastructure */}
      <TopLevelRoute
        path={`${FRONTEND_LOCALIZED_NEWS}/${LOCATION_SLUG_PATTERN}`}
        component={NewsfeedPage}
        active={features.newsfeed}
      />
      <TopLevelRoute
        path={`${FRONTEND_LOCALIZED_EVENTS}/${LOCATION_SLUG_PATTERN}`}
        component={CategorizedEventFeed}
        active={features.events}
      />
      {/* end of legacy routes */}
      <ContentLocationTopLevelRoute
        path={`${FRONTEND_PROFILE_FEED_PATH}/:profileType(${profileTypeRegex})/:profileId${FOLLOWERS_PARTIAL}`}
        component={ProfileFollowersPage}
      />
      <ContentLocationTopLevelRoute
        path={`${FRONTEND_PROFILE_FEED_PATH}/:profileType(${profileTypeRegex})/:profileId`}
        component={ProfileFeedPage}
      />
      {/* <LoginRequiredRoute
        path={`${FRONTEND_POST_PATH}/:id${FRONTEND_POST_REACTIONS_PATH}`}
        component={PostReactionsPage}
      /> */}
      {/* redirect posts/snapshot-{id} -> snapshots/{id} */}
      <ContentLocationTopLevelRoute
        path={`${FRONTEND_POST_PATH}/snapshot-:id`}
        render={({ match }) => <Redirect to={`${FRONTEND_SNAPSHOT_PATH}/${match.params.id}`} />}
      />
      {/* redirect posts/{id} -> articles/{id} */}
      <ContentLocationTopLevelRoute
        path={`${FRONTEND_POST_PATH}/:id`}
        render={({ match }) => <Redirect to={`${FRONTEND_ARTICLE_PATH}/${match.params.id}`} />}
      />
      <ContentLocationTopLevelRoute
        path={`${FRONTEND_ARTICLE_PATH}/:id`}
        component={ArticleDetailsPage}
      />
      <ContentLocationTopLevelRoute
        path={`${FRONTEND_SNAPSHOT_PATH}/:id`}
        component={SnapshotDetailsPage}
      />
      {/* <LoginRequiredRoute
        path={`${FRONTEND_EVENT_PATH}/:id${FRONTEND_POST_REACTIONS_PATH}`}
        component={PostReactionsPage}
        active={features.events}
      /> */}
      <ContentLocationTopLevelRoute
        path={`${FRONTEND_EVENT_PATH}/:id`}
        component={EventDetailsPage}
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${NEWS_FEED_PARTIAL}`}
        component={NewsfeedPage}
        active={features.newsfeed}
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${EVENTS_FEED_PARTIAL}`}
        component={CategorizedEventFeed}
        active={features.events}
        exact
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${EVENTS_FEED_PARTIAL}${CREATE_POST}`}
        component={CategorizedEventFeed}
        active={features.events}
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${EVENTS_FEED_PARTIAL}/${FILTER_PATTERN}`}
        component={EventCategoryFeedPage}
        active={features.events}
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${PUBLIC_PARTICIPATION_PARTIAL}`}
        component={ProjectsOverviewPage}
        active={features.publicParticipationProjects}
        exact
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${PUBLIC_PARTICIPATION_PARTIAL}${PROJECT_LIST_PARTIAL}`}
        component={ProjectsListPage}
        active={features.publicParticipationProjects}
        exact
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${PUBLIC_PARTICIPATION_PARTIAL}${PROJECT_MY_LIST_PARTIAL}`}
        component={ProjectsMyListPage}
        active={features.publicParticipationProjects}
        exact
      />
      <TopLevelRoute
        path={`${PUBLIC_PARTICIPATION_PARTIAL}/:slug/map`}
        component={ProjectDetailMapPage}
        active={features.publicParticipationProjects}
        exact
      />
      <TopLevelRoute
        path={`${PUBLIC_PARTICIPATION_PARTIAL}/:slug/meetings`}
        component={ProjectMeetingsPage}
        active={features.publicParticipationProjects}
        exact
      />
      <TopLevelRoute
        path={`${PUBLIC_PARTICIPATION_PARTIAL}/:slug${PROJECTS_MILESTONES_PARTIAL}`}
        component={ProjectMilestonesPage}
        active={features.publicParticipationProjects}
        exact
      />
      <TopLevelRoute
        path={`${PUBLIC_PARTICIPATION_PARTIAL}/:slug`}
        component={ProjectDetailPage}
        active={features.publicParticipationProjects}
        exact
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${INFRASTRUCTURE_PROJECTS_PARTIAL}`}
        component={InfrastructureProjectsOverviewPage}
        active={features.infrastructureProjects}
        exact
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${INFRASTRUCTURE_PROJECTS_PARTIAL}${PROJECT_LIST_PARTIAL}`}
        component={InfrastructureProjectsListPage}
        active={features.infrastructureProjects}
        exact
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${INFRASTRUCTURE_PROJECTS_PARTIAL}${PROJECT_MY_LIST_PARTIAL}`}
        component={InfrastructureProjectsMyListPage}
        active={features.infrastructureProjects}
        exact
      />
      <TopLevelRoute
        path={`${INFRASTRUCTURE_PROJECTS_PARTIAL}/:slug/map`}
        component={InfrastructureProjectDetailMapPage}
        active={features.infrastructureProjects}
        exact
      />
      <TopLevelRoute
        path={`${INFRASTRUCTURE_PROJECTS_PARTIAL}/:slug/meetings`}
        component={InfrastructureProjectMeetingsPage}
        active={features.infrastructureProjects}
        exact
      />
      <TopLevelRoute
        path={`${INFRASTRUCTURE_PROJECTS_PARTIAL}/:slug${PROJECTS_MILESTONES_PARTIAL}`}
        component={InfrastructureProjectMilestonesPage}
        active={features.infrastructureProjects}
        exact
      />
      <TopLevelRoute
        path={`${INFRASTRUCTURE_PROJECTS_PARTIAL}/:slug`}
        component={InfrastructureProjectDetailPage}
        active={features.infrastructureProjects}
        exact
      />
      <LoginRequiredRoute
        path={FRONTEND_NOTIFICATIONS_PATH}
        component={NotificationsPage}
        active={features.notifications}
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${FRONTEND_PROFILES_DIRECTORY}`}
        component={ProfilesDirectoryCategorizedPage}
        active={features.userDirectory}
        exact
      />
      <TopLevelRoute
        path={`/${LOCATION_SLUG_PATTERN}${FRONTEND_PROFILES_DIRECTORY}${PROFILES_DIRECTORY_CATEGORY_PATTERN}`}
        component={ProfilesDirectoryCategoryPage}
        active={features.userDirectory}
      />
      {shouldEnableMailboxRoute && (
        <LoginRequiredRoute
          path={FRONTEND_CONVERSATION_PATH}
          exact
          component={MailboxPage}
        />
      )}
      {shouldEnableMailboxRoute && (
        <LoginRequiredRoute
          path={`${FRONTEND_SEND_MESSAGE_CONVERSATION_PATH}:profileType(${userTypeRegex})/:profileId`}
          component={MailboxPage}
        />
      )}
      {shouldEnableMailboxRoute && (
        <LoginRequiredRoute
          path={`${FRONTEND_CONVERSATION_PATH}:conversationId(\\d+)`}
          component={MailboxPage}
        />
      )}

      <TopLevelRoute
        path={`${FRONTEND_GROUP_PATH}:id/members`}
        component={GroupMembersPage}
        active={features.groups}
      />
      <TopLevelRoute
        path={`${FRONTEND_GROUP_PATH}:id`}
        component={GroupFeedPage}
        active={features.groups}
      />
      <TopLevelRoute
        path={FRONTEND_GROUP_PATH}
        component={GroupsOverviewPage}
        active={features.groups}
      />
      <TopLevelRoute
        path={FRONTEND_NEW_PASSWORD}
        component={ResetPasswordPage}
      />
      <TopLevelRoute path={FRONTEND_SETTINGS_PATH} component={SettingsPage} />
      <TopLevelRoute
        path={`${FRONTEND_CHAT_PATH}/${CHAT_ROOM_PATTERN}`}
        component={TopicChatPage}
      />
      <TopLevelRoute path={FRONTEND_REDIRECT} component={RedirectPage} />
      <TopLevelRoute
        path={FRONTEND_LOCATION_PICKER_PATH}
        component={LocationPickerPage}
      />
      <LoginRequiredRoute
        path={FRONTEND_CREATE_PROFILE}
        active={navigationFeatureFlags.get(
          NavigationFeatureFlags.CREATE_PROFILE
        )}
        component={CreateProfilePage}
      />
      <RegistrationRoute
        path={FRONTEND_REGISTRATION}
        component={RegistrationPage}
      />
      <RegistrationRoute
        path={FRONTEND_APP_REGISTRATION}
        component={RegistrationPage}
      />
      <LogoutRequiredRoute path={FRONTEND_LOGIN} component={LoginPage} />
      <TopLevelRoute component={NotFoundPage} />
    </Switch>
  )
}
